import { createChevronLeftSvg, createChevronRightSvg } from "../icons";

import { t } from "i18n/i18n";

const SCROLL_STEP_PX = 200;
const TRANSITION_MS = 200;

function createScrollButton(
  svg: SVGSVGElement,
  label: string,
  side: "left" | "right",
): { wrapper: HTMLDivElement; button: HTMLButtonElement } {
  const wrapper = document.createElement("div");
  const sideClasses =
    side === "left"
      ? "left-0 bg-gradient-to-r pl-1.5 rounded-bl-md"
      : "right-0 justify-end bg-gradient-to-l pr-1.5 rounded-br-md";
  wrapper.className = `absolute inset-y-0 z-10 w-12 flex items-center opacity-0 pointer-events-none from-white from-75% ${sideClasses}`;
  wrapper.style.transition = `opacity ${TRANSITION_MS}ms ease-in-out`;

  const button = document.createElement("button");
  button.type = "button";
  button.title = label;
  button.ariaLabel = label;
  button.tabIndex = -1; // pointer-only
  button.className =
    "size-7 flex items-center justify-center rounded text-black-tint-20 hover:bg-blue-tint-95";

  button.appendChild(svg);
  wrapper.appendChild(button);

  return { wrapper, button };
}

/** Wraps the toolbar's existing content in a scrollable container with chevron scroll buttons on each side. */
export class ScrollableToolbar {
  private scrollContainer: HTMLDivElement;

  private leftWrapper: HTMLDivElement;

  private rightWrapper: HTMLDivElement;

  private leftBtn: HTMLButtonElement;

  private rightBtn: HTMLButtonElement;

  private resizeObserver: ResizeObserver;

  private onScroll = () => this.updateVisibility();

  private onScrollLeft = () =>
    this.scrollContainer.scrollBy({ left: -SCROLL_STEP_PX, behavior: "smooth" });

  private onScrollRight = () =>
    this.scrollContainer.scrollBy({ left: SCROLL_STEP_PX, behavior: "smooth" });

  constructor(toolbar: HTMLElement) {
    const scrollContainer = document.createElement("div");
    scrollContainer.className = "toolbar-scroll-container flex-1 min-w-0 flex items-center py-px";
    this.scrollContainer = scrollContainer;

    const contentWrapper = document.createElement("div");
    contentWrapper.className = "flex items-center w-max";

    // Move all existing toolbar content into the content wrapper
    while (toolbar.firstChild) {
      contentWrapper.appendChild(toolbar.firstChild);
    }
    scrollContainer.appendChild(contentWrapper);

    const left = createScrollButton(
      createChevronLeftSvg(),
      t("worksheet.rich_text_editor.scroll_left"),
      "left",
    );
    this.leftWrapper = left.wrapper;
    this.leftBtn = left.button;

    const right = createScrollButton(
      createChevronRightSvg(),
      t("worksheet.rich_text_editor.scroll_right"),
      "right",
    );
    this.rightWrapper = right.wrapper;
    this.rightBtn = right.button;

    toolbar.appendChild(this.leftWrapper);
    toolbar.appendChild(scrollContainer);
    toolbar.appendChild(this.rightWrapper);

    this.leftBtn.addEventListener("click", this.onScrollLeft);
    this.rightBtn.addEventListener("click", this.onScrollRight);
    scrollContainer.addEventListener("scroll", this.onScroll);

    this.resizeObserver = new ResizeObserver(() => this.updateVisibility());
    this.resizeObserver.observe(scrollContainer); // container width changes (e.g. editor resize)
    this.resizeObserver.observe(contentWrapper); // content width changes (e.g. panel switch)

    this.updateVisibility();
  }

  private updateVisibility(): void {
    const scrollLeft = Math.round(this.scrollContainer.scrollLeft);
    const scrollWidth = Math.round(this.scrollContainer.scrollWidth);
    const clientWidth = Math.round(this.scrollContainer.clientWidth);

    const canScrollLeft = scrollLeft > 0;
    const canScrollRight = scrollLeft + clientWidth < scrollWidth;

    for (const [wrapper, show] of [
      [this.leftWrapper, canScrollLeft],
      [this.rightWrapper, canScrollRight],
    ] as [HTMLDivElement, boolean][]) {
      if (wrapper.classList.contains("opacity-100") !== show) {
        wrapper.classList.toggle("opacity-100", show);
        wrapper.classList.toggle("opacity-0", !show);
        wrapper.classList.toggle("pointer-events-none", !show);
        wrapper.classList.toggle("pointer-events-auto", show);
      }
    }
  }

  resetScroll(): void {
    this.scrollContainer.scrollLeft = 0;
  }

  destroy(): void {
    this.leftBtn.removeEventListener("click", this.onScrollLeft);
    this.rightBtn.removeEventListener("click", this.onScrollRight);
    this.scrollContainer.removeEventListener("scroll", this.onScroll);
    this.resizeObserver.disconnect();
  }
}
